import UploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

interface ModalUploadDocumentProps {
	isOpen: boolean;
	onClose: () => void;
	onAttach: (file: File, documentType: string) => void;
	onRemove: () => void;
	existingFileName?: string | null;
	nameDocument?: string;
}

const normalizeFileName = (fileName: string): string => {
	return fileName
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/\s+/g, "_")
		.replace(/[^a-zA-Z0-9._]/g, "");
};

export const ModalUploadDocument: React.FC<ModalUploadDocumentProps> = ({
	isOpen,
	onClose,
	onAttach,
	onRemove,
	existingFileName,
	nameDocument = "documento",
}) => {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [documentType, setDocumentType] = useState<string>("");
	const supportedFormats = ["png", "jpg", "jpeg", "doc", "docx", "pdf", "odt"];

	const documentTypes = [
		{ value: "client_contract", label: "Contratos de serviços" },
		{ value: "client_adictive", label: "Aditivos de contratos" },
		{ value: "client", label: "Outros" },
	];

	useEffect(() => {
		if (existingFileName && isOpen) {
			setSelectedFile(new File([], existingFileName));
		} else if (!isOpen) {
			setSelectedFile(null);
			setDocumentType("");
			setError(null);
		}
	}, [existingFileName, isOpen]);

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			const fileExtension = file.name.split(".").pop()?.toLowerCase();

			if (!fileExtension || !supportedFormats.includes(fileExtension)) {
				setError(`Formato inválido. Permitidos: ${supportedFormats.join(", ").toUpperCase()}`);
				setSelectedFile(null);
			} else {
				const normalized = normalizeFileName(file.name);
				const normalizedFile = new File([file], normalized, { type: file.type });
				setSelectedFile(normalizedFile);
				setError(null);
			}
		}
	};

	const handleAttach = () => {
		if (!documentType) {
			setError("Selecione o tipo de documento.");
			return;
		}
		if (selectedFile) {
			onAttach(selectedFile, documentType);
		}
	};

	const handleRemoveFile = () => {
		setSelectedFile(null);
		onRemove();
	};

	const formatFileSize = (size: number) => {
		if (size < 1024) return `${size}B`;
		if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)}KB`;
		return `${(size / (1024 * 1024)).toFixed(2)}MB`;
	};

	const handleClose = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		setTimeout(() => {
			onClose();
		}, 0);
	};

	return (
		<Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
			<DialogTitle>Anexar {nameDocument}</DialogTitle>
			<DialogContent>
				{error && (
					<Typography
						variant="body2"
						sx={{
							color: "#e36b5b",
							bgcolor: "#fcedea",
							p: 1,
							borderRadius: 2,
							mb: 2,
						}}
					>
						{error}
					</Typography>
				)}

				{/* Campo: Tipo de documento */}
				<Box sx={{ mt: 2 }}>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Tipo de Documento
					</Typography>
					<Select fullWidth value={documentType} onChange={(e: SelectChangeEvent) => setDocumentType(e.target.value)} displayEmpty>
						<MenuItem value="" disabled>
							Selecione um tipo
						</MenuItem>
						{documentTypes.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</Box>

				{/* Upload visual */}
				{selectedFile ? (
					<Box
						sx={{
							border: "2px dashed #FFD700",
							borderRadius: 2,
							p: 4,
							mt: 2,
							textAlign: "center",
						}}
					>
						<Typography
							variant="body1"
							sx={{
								wordBreak: "break-word",
								textAlign: "center",
							}}
						>
							{selectedFile.name}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Tamanho: {selectedFile.size > 0 ? formatFileSize(selectedFile.size) : "Desconhecido"}
						</Typography>
						<Button color="error" startIcon={<DeleteIcon />} onClick={handleRemoveFile}>
							Apagar
						</Button>
					</Box>
				) : (
					<Box
						sx={{
							border: "2px dashed #FFD700",
							borderRadius: 2,
							p: 4,
							mt: 2,
							cursor: "pointer",
							textAlign: "center",
						}}
						onClick={() => document.getElementById("upload-input")?.click()}
					>
						<UploadIcon sx={{ fontSize: 48, color: "#666", mb: 1 }} />
						<Typography variant="body2" color="text.secondary">
							Clique para carregar o arquivo (máx. 50MB)
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Tipos: PNG, JPG, JPEG, DOC, DOCX, PDF, ODT
						</Typography>
					</Box>
				)}

				<input id="upload-input" type="file" style={{ display: "none" }} onChange={handleFileChange} />
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose}>Cancelar</Button>
				<Button onClick={handleAttach} disabled={!selectedFile || !documentType || !!error} variant="contained">
					Anexar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
